import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Skeleton } from "@mui/material";

import { EndorsementRoutes } from "@routing/paths";
import {
  DiscardEndorsementModal,
  EndorsementIneligibleModal,
} from "@components/EndorsementsModals";

import { EndorsementCard } from "./EndorsementCard";
import { AddEndorsementButton } from "./AddEndorsementButton";
import { EmptyEndorsementsList } from "./EmptyEndorsementsList";
import { useEndorsementsList } from "./hooks/useEndorsementsList";
import { PolicyDetailsContentProps } from "../../types";

interface Props {
  readonly policy?: PolicyDetailsContentProps["policy"];
  readonly loading?: PolicyDetailsContentProps["loading"];
}

export function EndorsementsList({ policy, loading }: Props) {
  const {
    blockingReason,
    canManageEndorsements,
    handleCreateNewEndorsement,
    handleDiscard,
    handleDiscardConfirm,
    handleModalClose,
    isDiscardModalOpen,
    isEligibilityLoading,
    openEndorsementModal,
    sortedEndorsements,
    setBlockingReason,
  } = useEndorsementsList(policy);

  if (!policy && loading) {
    return <Skeleton height={114} variant="rounded" />;
  }

  return (
    <>
      {sortedEndorsements.length === 0 ? (
        <EmptyEndorsementsList
          canManageEndorsements={canManageEndorsements}
          handleCreateNewEndorsement={handleCreateNewEndorsement}
          isEligibilityLoading={isEligibilityLoading}
        />
      ) : (
        <>
          <Stack direction="row" justifyContent="flex-end" mb={1.5}>
            <AddEndorsementButton
              canManageEndorsements={canManageEndorsements}
              handleCreateNewEndorsement={handleCreateNewEndorsement}
              isEligibilityLoading={isEligibilityLoading}
            />
          </Stack>
          <Paper variant="outlined" sx={{ overflow: "hidden" }}>
            <Stack divider={<Divider />} spacing={0}>
              {sortedEndorsements.map((endorsement) => (
                <EndorsementCard
                  key={endorsement.endorsementLocator}
                  endorsementId={endorsement.endorsementLocator}
                  effectiveDate={endorsement.effectiveDate}
                  status={endorsement.status}
                  reason={endorsement.note}
                  isSocotraNative={endorsement.isSocotraNative}
                  isReadOnly={!canManageEndorsements}
                  onUpdateEndorsement={() =>
                    openEndorsementModal(
                      "update",
                      EndorsementRoutes.INSURED_INFO,
                      endorsement.endorsementLocator
                    )
                  }
                  onIssueEndorsement={() =>
                    openEndorsementModal(
                      "issue",
                      EndorsementRoutes.CHECKOUT,
                      endorsement.endorsementLocator
                    )
                  }
                  onReviewIssue={() =>
                    openEndorsementModal(
                      "issue",
                      EndorsementRoutes.CHECKOUT,
                      endorsement.endorsementLocator
                    )
                  }
                  onUwReview={() =>
                    openEndorsementModal(
                      "review",
                      EndorsementRoutes.UW_REVIEW,
                      endorsement.endorsementLocator
                    )
                  }
                  onDiscard={() => {
                    handleDiscard(endorsement.endorsementLocator);
                  }}
                />
              ))}
            </Stack>
          </Paper>
          <Typography
            variant="body2"
            color="text.secondary"
            mt={2}
            fontStyle="italic">
            Note: Ready to Issue endorsements will be discarded automatically
            when a renewal is issued.
          </Typography>
        </>
      )}

      <DiscardEndorsementModal
        isModalOpen={isDiscardModalOpen}
        onClose={handleModalClose}
        onConfirm={handleDiscardConfirm}
      />

      {!!blockingReason && (
        <EndorsementIneligibleModal
          isModalOpen={!!blockingReason}
          reason={blockingReason}
          onClose={() => setBlockingReason(null)}
        />
      )}
    </>
  );
}
